import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { User, Briefcase, Calendar, FileText, Upload, Download, Trash2, Mail, Phone, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const DOC_TYPES = ["pan", "aadhaar", "passport", "salary_slip", "offer_letter", "joining_letter", "relieving_letter", "education", "other"];

export default function AdminCandidate360() {
  const { email } = useParams();
  const [data, setData] = useState(null);
  const [docType, setDocType] = useState("other");
  const fileRef = useRef(null);

  const load = () => api.get(`/admin/candidates/${encodeURIComponent(email)}/profile`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    const token = getToken();
    try {
      await fetch(`${API_BASE}/admin/candidates/${encodeURIComponent(email)}/documents`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      }).then((r) => { if (!r.ok) throw new Error("Upload failed"); });
      toast.success("Uploaded"); load();
    } catch { toast.error("Upload failed"); }
  };

  const downloadDoc = async (d) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = d.file_name; link.click();
    URL.revokeObjectURL(url);
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete ${d.file_name}?`)) return;
    await api.delete(`/admin/documents/${d.id}`); load();
  };

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;

  const latest = data.applications[0] || {};

  return (
    <div className="space-y-6" data-testid="admin-candidate-360">
      <div className="flex items-center gap-2">
        <Link to="/admin/talent-search" className="text-sm text-slate-500 hover:text-primary"><ArrowLeft className="mr-1 inline h-3.5 w-3.5" />Back to search</Link>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-xl bg-primary/10 font-heading text-2xl font-bold text-primary">
            {(latest.candidate_name || email).charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-semibold text-slate-900">{latest.candidate_name || email}</h1>
            <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{email}</div>
              {latest.phone && <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{latest.phone}</div>}
              {latest.current_company && <div>@{latest.current_company}</div>}
              {latest.experience_years != null && <Badge variant="outline">{latest.experience_years} yrs exp</Badge>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Applications timeline */}
        <div className="lg:col-span-2">
          <SectionTitle icon={Briefcase}>Applications ({data.applications.length})</SectionTitle>
          <div className="mt-3 space-y-3">
            {data.applications.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{a.job_title || "General resume drop"}</div>
                    <div className="text-xs text-slate-500">{new Date(a.applied_at).toLocaleString()}</div>
                  </div>
                  <Badge className="capitalize">{a.status}</Badge>
                </div>
                {(a.tags || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{a.tags.map((t) => <Badge key={t} className="bg-slate-100 text-slate-700">{t}</Badge>)}</div>}
                {(a.notes || []).length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-600">
                    <span className="font-semibold uppercase tracking-widest text-slate-400">Notes:</span> {(a.notes || []).slice(-2).map((n, i) => <div key={i} className="mt-1 text-slate-700">• {n.text}</div>)}
                  </div>
                )}
              </div>
            ))}
            {data.applications.length === 0 && <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-400">No applications on record.</div>}
          </div>

          {data.interviews.length > 0 && (
            <>
              <SectionTitle icon={Calendar} className="mt-6">Interviews ({data.interviews.length})</SectionTitle>
              <div className="mt-3 space-y-2">
                {data.interviews.map((iv) => (
                  <div key={iv.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{iv.round_name} · {iv.job_title}</div>
                      <Badge className="capitalize">{iv.status?.replace("_", " ")}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">{new Date(iv.scheduled_at).toLocaleString()} · {iv.mode} · {iv.panelist_name || "—"}</div>
                    {iv.feedback_notes && <div className="mt-1 text-xs text-slate-600">{iv.feedback_notes}</div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {data.placements.length > 0 && (
            <>
              <SectionTitle icon={ShieldCheck} className="mt-6">Placements ({data.placements.length})</SectionTitle>
              <div className="mt-3 space-y-2">
                {data.placements.map((p) => (
                  <div key={p.id} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{p.job_title} @ {p.client_name}</div>
                      <Badge className="bg-emerald-100 text-emerald-700 capitalize">{p.status?.replace("_", " ")}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">CTC ₹{(p.ctc_offered || 0).toLocaleString("en-IN")} · Joining {p.joining_date || "—"} · Fee ₹{(p.fee_amount || 0).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Documents vault */}
        <div>
          <SectionTitle icon={FileText}>Compliance Vault ({data.documents.length})</SectionTitle>
          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex gap-2">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="doc-upload"><Upload className="h-3.5 w-3.5" /></Button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </div>
            <div className="mt-3 space-y-2">
              {data.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm" data-testid={`doc-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-slate-800">{d.file_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{d.doc_type} · {(d.file_size / 1024).toFixed(0)} KB</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)}><Download className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteDoc(d)}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                  </div>
                </div>
              ))}
              {data.documents.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No documents yet</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, className = "" }) {
  return (
    <div className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-slate-500 ${className}`}>
      <Icon className="h-4 w-4 text-primary" />{children}
    </div>
  );
}
